import { ethers } from 'ethers'
import { CL3_SWAP_ROUTER, CL_SWAP_ROUTER } from '../addresses'
import { CL_SWAP_ROUTER_ABI } from '../abi'
import { candidateV3Path, poolFeesOrSpacings } from '../path'
import type { Adapter, ExecuteArgs } from './types'
import type { RouteCandidate } from '../types'

/**
 * Aerodrome Slipstream — concentrated liquidity, factories 1 and 3.
 *
 * It is v3-periphery rather than SwapRouter02, and the difference is exactly
 * where a copied adapter would go wrong. Read from the deployed contract's own
 * verified source (`contracts/periphery/SwapRouter.sol` of `0xBE6D8f0d…` on
 * Base, Sourcify, chain 8453):
 *
 *   - there are no `MSG_SENDER` / `ADDRESS_THIS` sentinels. The router is named
 *     by `address(0)`: "allow swapping to the router address with address 0",
 *     and `exactInputInternal` rewrites it to `address(this)`. Passing
 *     `address(2)` here would send the proceeds to that address and lose them.
 *   - the deadline is a field of the params struct, enforced by
 *     `checkDeadline`, so `multicall` takes only the call array.
 *   - `exactInputSingle` already calls `refundETH()` itself, so no extra call
 *     is needed for a native-in swap.
 */
export const CL_ROUTER_SELF = '0x0000000000000000000000000000000000000000'

/**
 * Factory 3's periphery is the same source at a different address, so one
 * adapter serves both — but a Slipstream router validates its callbacks
 * against **its own** factory's init code hash, so sending a factory-3 route to
 * the factory-1 router reverts inside the pool callback with nothing readable
 * attached. The router is therefore chosen from the route, never assumed.
 */
export function clRouterFor(venue: RouteCandidate['venue']): string {
  return venue === 'aero-cl3' ? CL3_SWAP_ROUTER : CL_SWAP_ROUTER
}

const iface = new ethers.utils.Interface(CL_SWAP_ROUTER_ABI as unknown as string[])

export const aeroClAdapter: Adapter = {
  spender(args: ExecuteArgs): string | null {
    if (args.tokenIn.isNative) return null
    return clRouterFor((args.quote.candidate as RouteCandidate).venue)
  },

  async buildTx(args: ExecuteArgs): Promise<ethers.PopulatedTransaction> {
    const candidate = args.quote.candidate as RouteCandidate
    const router = clRouterFor(candidate.venue)
    const amountIn = ethers.BigNumber.from(args.quote.amountIn.toString())
    const minOut = ethers.BigNumber.from(args.minOut.toString())
    const deadline = ethers.BigNumber.from(args.deadline.toString())
    const recipient = args.tokenOut.isNative ? CL_ROUTER_SELF : args.account

    const swapData =
      candidate.pools.length === 1
        ? iface.encodeFunctionData('exactInputSingle', [
            {
              tokenIn: candidate.path[0],
              tokenOut: candidate.path[1],
              tickSpacing: poolFeesOrSpacings(candidate.pools)[0],
              recipient,
              deadline,
              amountIn,
              amountOutMinimum: minOut,
              sqrtPriceLimitX96: 0
            }
          ])
        : iface.encodeFunctionData('exactInput', [
            {
              path: candidateV3Path(candidate),
              recipient,
              deadline,
              amountIn,
              amountOutMinimum: minOut
            }
          ])

    if (!args.tokenOut.isNative) {
      return {
        to: router,
        data: swapData,
        value: args.tokenIn.isNative ? amountIn : ethers.constants.Zero
      }
    }

    return {
      to: router,
      data: iface.encodeFunctionData('multicall(bytes[])', [
        [swapData, iface.encodeFunctionData('unwrapWETH9', [minOut, args.account])]
      ]),
      value: args.tokenIn.isNative ? amountIn : ethers.constants.Zero
    }
  }
}
